import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import toast, { Toaster } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { dealerService } from '../services/dealerService';
import { DEALER_ID } from '../constants/dealer';

// Simple pin icon built from inline HTML so we don't depend on Leaflet's
// image assets (which break under Vite's bundler without extra config).
const pinIcon = L.divIcon({
  className: '',
  html: '<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:#1e3a8a;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 18],
});

// Coordinates for SE Yard Solutions lots, keyed by city.
const LOT_COORDS = {
  'fort worth': { lat: 32.9342, lng: -97.3120 },
  'paradise': { lat: 33.1593, lng: -97.6889 },
};

const coordsForLot = (lot) => {
  const city = (lot.city || '').trim().toLowerCase();
  return LOT_COORDS[city] || LOT_COORDS['fort worth'];
};

const Contact = () => {
  const [lots, setLots] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await dealerService.getDealerById(DEALER_ID);
      if (active && data?.lot_locations?.length) {
        setLots(data.lot_locations);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please add your name, email and a message.');
      return;
    }

    setSubmitting(true);
    const parts = form.name.trim().split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ') || '';

    // NOTE: the real `leads` schema has no inquiry_type/source columns (those live
    // on a separate contact_inquiries table). The valid columns are lead_type
    // (constrained) and lead_source. 'information' + 'website' route contact-page
    // submissions into Edwin's Leads tab, which is the spec's intent.
    const { error } = await supabase.from('leads').insert([{
      dealer_id: DEALER_ID,
      first_name: firstName,
      last_name: lastName,
      email: form.email,
      phone: form.phone,
      message: form.message,
      lead_type: 'information',
      lead_source: 'website',
      status: 'new',
    }]);

    setSubmitting(false);

    if (error) {
      console.error('Contact lead insert failed:', error);
      toast.error('Something went wrong. Please call us instead.');
      return;
    }

    toast.success("Thanks. We'll get back to you shortly.");
    setForm({ name: '', phone: '', email: '', message: '' });
  };

  const inputClass =
    'w-full border border-secondary-200 rounded-lg px-4 py-3 text-secondary-900 focus:ring-1 focus:ring-primary-900 focus:border-primary-900 outline-none';

  return (
    <>
      <Helmet>
        <title>Contact SE Yard Solutions | Sheds in Dallas-Fort Worth</title>
        <meta
          name="description"
          content="Call SE Yard Solutions or message us about sheds on our Fort Worth, Texas lot. Free delivery within 100 miles."
        />
      </Helmet>

      <Toaster position="top-right" />

      <div className="min-h-screen bg-white pt-16">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          {/* 1) Phone block */}
          <section className="text-center mb-16">
            <a href="tel:+16822040561" className="text-5xl md:text-6xl font-bold text-primary-900">
              (682) 204-0561
            </a>
            <p className="mt-4 text-secondary-600">We answer the phone Mon-Sat, 8am to 6pm.</p>
          </section>

          {/* 2) Lot addresses + maps */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-8">{lots.length === 1 ? 'Our lot' : 'Our lots'}</h2>
            {lots.length === 0 ? (
              <p className="text-secondary-600">Lot locations are being updated. Call us for directions.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-8">
                {lots.map((lot, i) => {
                  const center = coordsForLot(lot);
                  return (
                    <div key={lot.id || i} className="rounded-lg border border-secondary-200 overflow-hidden">
                      <div className="p-5">
                        <h3 className="font-bold text-xl text-secondary-900">{(lot.name || '').trim()}</h3>
                        <p className="mt-1 text-secondary-600">{(lot.address || '').trim()}</p>
                      </div>
                      <MapContainer
                        center={[center.lat, center.lng]}
                        zoom={12}
                        scrollWheelZoom={false}
                        style={{ height: '240px', width: '100%' }}
                      >
                        <TileLayer
                          attribution='&copy; OpenStreetMap contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[center.lat, center.lng]} icon={pinIcon}>
                          <Popup>{(lot.name || '').trim()}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* 3) Message form */}
          <section>
            <h2 className="text-3xl font-bold text-secondary-900 mb-8">Send us a message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input className={inputClass} type="text" name="name" placeholder="Your name" value={form.name} onChange={handleChange} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={inputClass} type="tel" name="phone" placeholder="Phone" value={form.phone} onChange={handleChange} />
                <input className={inputClass} type="email" name="email" placeholder="Email" value={form.email} onChange={handleChange} />
              </div>
              <textarea className={inputClass} name="message" rows={5} placeholder="What can we help you with?" value={form.message} onChange={handleChange} />
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-800 disabled:opacity-60"
              >
                {submitting ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </>
  );
};

export default Contact;
